import { useMemo } from "react";
import { useGetInvitations } from "./useGetUserInvitations";
import { useGetJoinRequests } from "./useGetUserJoinRequests";
import { useGetOrganizations } from "./useGetUserOrganizations";
import { getPlanName } from "src/services/plans";

export type UserOrganization = {
  id: string;
  name: string;
  department?: string;
  license?: string;
  role: string;
  state?: string;
};

export const useGetUserMergedOrganizations = () => {
  const { loading: organizationsLoading, error: organizationsError, organizations } = useGetOrganizations();
  const { loading: joinRequestsLoading, error: joinRequestsError, joinRequests } = useGetJoinRequests();
  const { loading: invitationsLoading, error: invitationsError, invitations } = useGetInvitations();

  const mergedOrganizations = useMemo<UserOrganization[]>(() => {
    const fromOrgs = organizations.map(({ id, name, department, license, role }) => ({
      id,
      name,
      department,
      license: getPlanName(license),
      role,
    }));

    const fromJoinRequests = joinRequests.map(({ business, role, state }) => ({
      id: business.id,
      name: business.name,
      department: business.department,
      license: getPlanName(business.license),
      role,
      state,
    }));

    const fromInvitations = invitations.map(({ business, role, state }) => {
      let changedState = "invited";

      if (state !== "pending") {
        changedState = state;
      }

      return {
        id: business.id,
        name: business.name,
        department: business.department,
        license: getPlanName(business.license),
        role,
        state: changedState,
      };
    });

    return [...fromOrgs, ...fromJoinRequests, ...fromInvitations];
  }, [organizations, joinRequests, invitations]);

  const isLoading = organizationsLoading || joinRequestsLoading || invitationsLoading;
  const isError = !!(organizationsError || joinRequestsError || invitationsError);

  return {
    loading: isLoading,
    error: isError,
    organizations: mergedOrganizations,
  };
};
